import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Ban,
  RefreshCw,
  Loader2,
  Clock,
  Shield,
  AlertTriangle,
  Eye,
  EyeOff
} from "lucide-react";
import { API } from "../App";

const APIKeysManager = ({ getAdminHeaders }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  const [showFullKey, setShowFullKey] = useState(false);
  
  const [newKey, setNewKey] = useState({
    name: "",
    description: "",
    expires_in_days: null
  });

  const expirationOptions = [
    { value: "none", label: "Sin expiración", days: null },
    { value: "7", label: "7 días", days: 7 },
    { value: "30", label: "30 días", days: 30 },
    { value: "90", label: "90 días", days: 90 },
    { value: "180", label: "6 meses", days: 180 },
    { value: "365", label: "1 año", days: 365 },
  ];

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${API}/admin/api-keys`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys || []);
      }
    } catch (error) {
      console.error("Error fetching API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKey.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/admin/api-keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders()
        },
        credentials: "include",
        body: JSON.stringify(newKey),
      });

      if (response.ok) {
        const data = await response.json();
        setNewlyCreatedKey(data);
        setShowCreateDialog(false);
        setShowKeyDialog(true);
        setNewKey({ name: "", description: "", expires_in_days: null });
        fetchApiKeys();
        toast.success("API Key creada exitosamente");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear API Key");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/admin/api-keys/${keyId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("API Key eliminada");
        fetchApiKeys();
      } else {
        toast.error("Error al eliminar API Key");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/admin/api-keys/${keyId}/revoke`, {
        method: "PUT",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("API Key revocada");
        fetchApiKeys();
      } else {
        toast.error("Error al revocar API Key");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleActivateKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/admin/api-keys/${keyId}/activate`, {
        method: "PUT",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("API Key activada");
        fetchApiKeys();
      } else {
        toast.error("Error al activar API Key");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
    toast.success("Copiado al portapapeles");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (key) => {
    if (!key.is_active) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
          <Ban className="w-3 h-3" /> Revocada
        </span>
      );
    }
    if (key.is_expired) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Expirada
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
        <Shield className="w-3 h-3" /> Activa
      </span>
    );
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-yellow-400" />
              Gestión de API Keys
            </CardTitle>
            <CardDescription className="text-slate-400">
              Crea y administra claves de API para integraciones externas
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva API Key
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <Key className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay API Keys creadas</p>
            <p className="text-sm text-slate-500 mt-1">Crea una API Key para comenzar a integrar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <div 
                key={key.key_id} 
                className={`p-4 rounded-lg border transition-colors ${
                  !key.is_active || key.is_expired
                    ? 'bg-slate-900/30 border-slate-700/50 opacity-75'
                    : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-medium text-white">{key.name}</h4>
                      {getStatusBadge(key)}
                    </div>
                    
                    {key.description && (
                      <p className="text-sm text-slate-400 mb-2">{key.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 mb-3">
                      <code className="text-sm text-slate-300 bg-slate-800 px-3 py-1 rounded font-mono">
                        {key.api_key_masked || key.api_key_preview}
                      </code>
                      <button
                        onClick={() => copyToClipboard(key.api_key_masked || key.api_key_preview, key.key_id)}
                        className="p-1 text-slate-400 hover:text-white transition-colors"
                        title="Copiar clave parcial"
                      >
                        {copiedKeyId === key.key_id ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Creada: {formatDate(key.created_at)}
                      </span>
                      {key.expires_at && (
                        <span className={`flex items-center gap-1 ${key.is_expired ? 'text-orange-400' : ''}`}>
                          <AlertTriangle className="w-3 h-3" />
                          Expira: {formatDate(key.expires_at)}
                        </span>
                      )}
                      {key.last_used_at && (
                        <span>Último uso: {formatDate(key.last_used_at)}</span>
                      )}
                      {key.usage_count > 0 && (
                        <span>Usos: {key.usage_count.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {key.is_active && !key.is_expired ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/20"
                        onClick={() => handleRevokeKey(key.key_id)}
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        Revocar
                      </Button>
                    ) : !key.is_expired && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                        onClick={() => handleActivateKey(key.key_id)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Activar
                      </Button>
                    )}
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-800 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">¿Eliminar API Key?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Esta acción no se puede deshacer. La clave "{key.name}" dejará de funcionar inmediatamente para todas las integraciones.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-slate-700 text-white border-slate-600">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteKey(key.key_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <h4 className="text-sm font-medium text-blue-300 mb-2">Uso de API Keys</h4>
          <p className="text-xs text-blue-200/80">
            Incluye tu API Key en el header de las peticiones:
          </p>
          <code className="block mt-2 text-xs bg-blue-600/20 px-3 py-2 rounded text-blue-100 font-mono">
            Authorization: Bearer ok_tu_api_key_aqui
          </code>
        </div>
      </CardContent>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-yellow-400" />
              Nueva API Key
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Crea una nueva clave de API para integraciones externas
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={newKey.name}
                onChange={(e) => setNewKey({...newKey, name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="Mi Integración"
              />
            </div>

            <div>
              <Label className="text-slate-300">Descripción (opcional)</Label>
              <Input
                value={newKey.description}
                onChange={(e) => setNewKey({...newKey, description: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="Integración con sistema externo..."
              />
            </div>

            <div>
              <Label className="text-slate-300">Expiración</Label>
              <Select
                value={newKey.expires_in_days === null ? "none" : newKey.expires_in_days.toString()}
                onValueChange={(value) => {
                  const option = expirationOptions.find(o => o.value === value);
                  setNewKey({...newKey, expires_in_days: option?.days ?? null});
                }}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Seleccionar expiración" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {expirationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white hover:bg-slate-700">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-300">
                <strong>Importante:</strong> La clave completa solo se mostrará una vez después de crearla. Asegúrate de guardarla en un lugar seguro.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateKey} disabled={creating} className="bg-red-600 hover:bg-red-700">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear API Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show New Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <Check className="w-5 h-5" />
              API Key Creada
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Guarda esta clave en un lugar seguro. No podrás verla de nuevo.
            </DialogDescription>
          </DialogHeader>

          {newlyCreatedKey && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Tu nueva API Key:</span>
                  <button
                    onClick={() => setShowFullKey(!showFullKey)}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {showFullKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-green-400 bg-slate-800 px-3 py-2 rounded font-mono break-all">
                    {showFullKey ? newlyCreatedKey.api_key : "••••••••••••••••••••••••••••••••••••••••"}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey.api_key, "new-key")}
                    className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded transition-colors"
                  >
                    {copiedKeyId === "new-key" ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-300">
                  <strong>¡Atención!</strong> Esta es la única vez que verás la clave completa. Cópiala ahora y guárdala de forma segura.
                </p>
              </div>

              {newlyCreatedKey.expires_at && (
                <p className="text-sm text-slate-400">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Expira: {formatDate(newlyCreatedKey.expires_at)}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              onClick={() => {
                setShowKeyDialog(false);
                setNewlyCreatedKey(null);
                setShowFullKey(false);
              }} 
              className="bg-green-600 hover:bg-green-700"
            >
              Entendido, la guardé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default APIKeysManager;
